import { venueTypesAPIDict } from 'api/venueTypesApi';
import { RequirementType, venueTypes } from 'constants/venueTypes';
import queryString from 'query-string';
import { mToKm, miToKm, takeFirstIfArray, toMeters } from '../../util';

export type TPlaceQuery = Partial<{
    q: string; // semantic name of the place
    place_id: string; // google place id
    lat: number;
    lng: number;
}>;

type TTypes = BizlyAPI.Venue.Types;

// formats values from the URL params to the form data
export function queryParamsToFormFilters(params: string): BizlyAPI.VenueFilterValue & { venueId?: string | number } {
    const { types, guests } = queryString.parse(params, { arrayFormat: 'none' });
    const { dinovaOnly, preferredOnly, eventSpacesAvailable, guestRoomsAvailable } = queryString.parse(params, {
        parseBooleans: true,
        arrayFormat: 'none',
    });
    const parsed = queryString.parse(params, { parseNumbers: true, arrayFormat: 'none' });
    const radius = parsed.radius as Nullable<Distance.Meter>;

    return {
        dinovaOnly: !!dinovaOnly,
        eventSpacesAvailable: !!eventSpacesAvailable,
        guestRoomsAvailable: !!guestRoomsAvailable,
        types: types ? toArray(types as TTypes[]) : undefined,
        brandIds: parsed.brandIds ? toArray(parsed.brandIds as number[]) : undefined,
        preferredOnly: !!preferredOnly,
        radius: radius && radius !== null && typeof radius === 'number' ? radius : undefined,
        radiusKm: radius && radius !== null && typeof radius === 'number' ? mToKm(radius) : undefined,
        guests: guests ? Number(guests) : undefined,
        venueId: parsed.venueId as string | number | undefined,
        starRatings: parsed.starRatings
            ? toArray(parsed.starRatings)
                  .map(Number)
                  .filter(rating => !isNaN(rating) && rating !== 0)
            : undefined,
    };
}

// a "place" must have either the semantic name (q) and the place id or lat/lng
export function queryParamsToPlace(params: string) {
    const parsed = queryString.parse(params);
    const { q, place_id, lat, lng } = takeFirstIfArray(parsed as Record<string, string | string[] | undefined>);

    if (!q) return undefined;

    const parsedLat = (lat && !isNaN(parseFloat(lat)) && parseFloat(lat)) || undefined;
    const parsedLng = (lng && !isNaN(parseFloat(lng)) && parseFloat(lng)) || undefined;

    const hasPlace = place_id || (parsedLat && parsedLng);
    if (!hasPlace) return undefined;

    return { q, place_id: place_id || undefined, lat: parsedLat, lng: parsedLng };
}

export function parseMapVisibility(params: string): boolean {
    const parsed = queryString.parse(params);
    const { showMap } = takeFirstIfArray(parsed as Record<string, string | string[] | undefined>);

    return showMap?.toLocaleLowerCase() !== 'false';
}

// converts form data and place query into URL params
export function toQueryParams(
    filters: BizlyAPI.VenueFilterValue & TPlaceQuery & { showMap?: boolean; venueId?: string | number }
): BizlyAPI.VenueFacets & TPlaceQuery & { venueId?: string | number } {
    const { radiusKm, starRatings, types, ...applicableFilters } = filters;

    // Filter out invalid types (e.g., character arrays from string splitting bug)
    const validTypes =
        types && Array.isArray(types) && types.length > 0
            ? types.filter(type => typeof type === 'string' && type.length > 1) // Only valid venue type strings
            : undefined;

    return {
        ...applicableFilters,
        // Only include types if there are valid venue types selected
        ...(validTypes && validTypes.length > 0 ? { types: validTypes } : {}),
        // Only include starRatings if there are selected ratings
        ...(starRatings && starRatings.length > 0 ? { starRatings } : {}),
    };
}

// converts an array or non-array value into an array
// Note: For strings, we need to wrap them in an array first to avoid splitting into characters
function toArray<T>(singleOrArray: T | Array<T>): Array<T> {
    if (singleOrArray === null || singleOrArray === undefined) {
        return [];
    }
    // If it's already an array, return it
    if (Array.isArray(singleOrArray)) {
        return singleOrArray;
    }
    // For strings, wrap in array to prevent character splitting
    if (typeof singleOrArray === 'string') {
        return [singleOrArray] as Array<T>;
    }
    // For other types, use concat
    return ([] as Array<T>).concat(singleOrArray);
}

export const parseEventSearchParams = (
    { venueSearchParameters, requirements }: Bizly.Event,
    isEnterprise = false,
    showTestingVenues = false
) => {
    const { radius, brandIds, types, ...params } = venueSearchParameters ?? {};
    let requirementTypes: BizlyAPI.Venue.Types[] = [];

    // Get all available venue types from the API dictionary
    const allVenueTypes: BizlyAPI.Venue.Types[] = Object.values(venueTypesAPIDict({ showTestingVenues })).map(
        type => type.id as BizlyAPI.Venue.Types
    );

    if (requirements && requirements.length > 0) {
        const requiredVenueTypes = requirements.reduce(
            (types, venueType) => [
                ...types,
                ...((venueTypes[venueType as RequirementType] || []) as BizlyAPI.Venue.Types[]),
            ],
            [] as BizlyAPI.Venue.Types[]
        );

        // For enterprise users, default to all venue types instead of just corporate office
        if (isEnterprise) {
            requirementTypes = allVenueTypes;
        } else {
            requirementTypes.push(...requiredVenueTypes);
        }

        requirementTypes = Array.from(new Set(requirementTypes));
    }
    return {
        ...params,
        types: requirementTypes.length > 0 ? requirementTypes : types,
        ...(radius ? { radius } : { radius: toMeters(15 as Distance.Mile), radiusKm: miToKm(15 as Distance.Mile) }),
        ...(brandIds ? { brandIds } : { brandIds: undefined }),
        dinovaOnly: params.dinovaOnly ?? false,
        preferredOnly: params.preferredOnly ?? false,
    };
};

export function sortAndStringify(input: unknown): string {
    // If input is an array, map over its elements and sort/stringify each element
    if (Array.isArray(input)) {
        return `[${input.map(sortAndStringify).join(',')}]`;
    }

    // If input is an object, recursively sort its keys and stringify each value
    if (input !== null && typeof input === 'object') {
        const sortedKeys = Object.keys(input as Record<string, unknown>).sort();
        const sortedObject = sortedKeys.reduce(
            (acc: Record<string, string>, key: string) => {
                acc[key] = sortAndStringify((input as Record<string, unknown>)[key]);
                return acc;
            },
            {} as Record<string, string>
        );
        return `{${sortedKeys.map(key => `"${key}":${sortedObject[key]}`).join(',')}}`;
    }

    // For primitive values, directly stringify
    return JSON.stringify(input);
}
